import { EventPeriodEnum } from '../../../utils/api';
import { PeriodCount, periodLabel } from '../../../helpers/matchTiming';

/** Capture freshness is deliberately stricter than the upstream viewer grace. */
export const PHONE_STALE_MS = 2500;

export interface ClockSocket {
    onopen: ((event: Event) => void) | null;
    onmessage: ((event: MessageEvent) => void) | null;
    onclose: ((event: CloseEvent) => void) | null;
    onerror: ((event: Event) => void) | null;
    close(): void;
}
export interface ClockAccess { scoreboardUrl: string; validUntil: number; }
export interface ClockSelection {
    period: number;
    periodCount: PeriodCount;
    periodLengthMinutes: number;
    pso: boolean;
}
export interface ClockSnapshot {
    eligible: boolean;
    seconds?: number;
    period?: number;
    status: string;
    ageMs?: number;
    unavailableReason?: 'period-mismatch' | 'break';
}
interface PhoneSample {
    period: number;
    segmentMinutes: number;
    gameMs: number;
    refAt: number;
    running: boolean;
    mode: 'game' | 'timeout' | 'break';
    breakAfter?: number;
    incompatible: boolean;
}
interface ClockEnvironment {
    monotonicNow(): number;
    wallNow(): number;
    random(): number;
    links(): Promise<ClockAccess>;
    socket(url: string): ClockSocket;
}
const integer = (value: unknown): value is number =>
    typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

/** Owns only countdown data. Never receives or emits an Arena Match or command. */
export class LiveClockClient {
    private socket?: ClockSocket;
    private session = 0;
    private active = false;
    private retry?: ReturnType<typeof setTimeout>;
    private pulse?: ReturnType<typeof setInterval>;
    private attempt = 0;
    private hasConnected = false;
    private accessRenewalUsed = false;
    private accessUnavailable = false;
    private expires = Infinity;
    private status = 'Connecting';
    private sample?: PhoneSample;
    private seq = -1;
    private phoneSeq = -1;
    private offsets: number[] = [];
    private sampleReceived = 0;
    private sampleWall = 0;
    private sampleServerOffset = 0;
    private awaitingFreshSample = true;
    private trusted = new Map<number, number>();
    private readonly clientId = `arena-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;

    constructor(private matchId: number, private env: ClockEnvironment, private changed = () => {}) {}

    async start(): Promise<void> {
        if (this.active) return;
        this.active = true;
        this.hasConnected = false;
        this.accessRenewalUsed = false;
        this.accessUnavailable = false;
        this.expires = Infinity;
        this.status = 'Connecting';
        this.attempt = 0;
        this.pulse = setInterval(() => {
            if (this.env.wallNow() > this.expires) {
                const mayRenew = !this.accessUnavailable || !this.accessRenewalUsed;
                this.expires = Infinity;
                if (mayRenew) {
                    this.accessRenewalUsed = true;
                    this.status = 'Access expired · renewing';
                    void this.connect();
                }
            }
            this.changed();
        }, 100);
        await this.connect();
    }

    stop(): void {
        this.active = false;
        this.session++;
        clearTimeout(this.retry);
        clearInterval(this.pulse);
        this.socket?.close();
        this.socket = undefined;
        this.sample = undefined;
    }

    /** A visibility/sleep boundary needs another actual phone sample. */
    requireFreshSample(): void {
        this.awaitingFreshSample = true;
        if (!this.accessUnavailable && this.status !== 'Disconnected') this.status = 'Phone updates stopped';
        this.changed();
    }

    lastTrusted(period: number): number | undefined { return this.trusted.get(period); }

    snapshot(selection: ClockSelection): ClockSnapshot {
        if (selection.pso) return { eligible: false, seconds: 0, period: EventPeriodEnum.Overtime, status: 'PSO · 00:00 · Live clock suspended' };
        const accessStatus = this.accessUnavailable ? 'Access unavailable'
            : this.env.wallNow() >= this.expires ? 'Access expired · renewing' : undefined;
        const sample = this.sample;
        if (!sample) return { eligible: false, status: accessStatus ?? this.status };
        const now = this.env.monotonicNow();
        // Date catches browser sleep on platforms whose performance clock pauses.
        const elapsed = Math.max(now - this.sampleReceived, this.env.wallNow() - this.sampleWall);
        const ageMs = Math.max(0, this.sampleReceived + this.sampleServerOffset - sample.refAt) + elapsed;
        const periodNumber = sample.breakAfter ?? sample.period;
        const period = periodNumber >= 5 ? EventPeriodEnum.Overtime : EventPeriodEnum.First_Period + periodNumber - 1;
        const seconds = sample.breakAfter != null ? 0 : Math.max(0,
            sample.gameMs - (sample.running && sample.mode === 'game' ? Math.min(ageMs, PHONE_STALE_MS) : 0)) / 1000;
        // MatchDetailVm.matchSegmentLength configures both regulation and OT in Arena.
        const lengthMismatch = sample.segmentMinutes !== selection.periodLengthMinutes;
        const incompatible = sample.incompatible || (periodNumber < 5 && periodNumber > selection.periodCount)
            || lengthMismatch
            || sample.gameMs > sample.segmentMinutes * 60000;
        let status = sample.breakAfter != null ? 'Break' : sample.mode === 'timeout' ? 'Timeout'
            : sample.running ? 'Phone clock running' : 'Clock stopped';
        if (this.awaitingFreshSample) status = `${accessStatus ?? this.status} · stale · ${(ageMs / 1000).toFixed(1)}s old`;
        else if (accessStatus) status = accessStatus;
        else if (lengthMismatch) {
            const segment = periodNumber >= 5 ? 'OT' : 'period';
            status = `Phone ${segment} length ${sample.segmentMinutes} min · Match ${segment} length ${selection.periodLengthMinutes} min · Live capture unavailable`;
        } else if (incompatible) status = 'Phone timing disagrees with Match';
        else if (ageMs >= PHONE_STALE_MS) status = `Phone updates stopped · ${(ageMs / 1000).toFixed(1)}s old`;
        else if (period !== selection.period) {
            const selectedLabel = periodLabel(selection.period, selection.periodCount);
            const phoneLabel = periodNumber >= 5 ? `OT ${periodNumber - 4}` : periodLabel(period, selection.periodCount);
            status = `Selected ${selectedLabel} · Phone ${phoneLabel} · Live capture unavailable`;
        }
        const fresh = !this.awaitingFreshSample && !accessStatus && !incompatible && ageMs < PHONE_STALE_MS;
        if (fresh) this.trusted.set(period, seconds);
        const unavailableReason = fresh && period !== selection.period && sample.mode !== 'timeout'
            ? sample.breakAfter != null ? 'break' : 'period-mismatch'
            : undefined;
        return { eligible: fresh && period === selection.period, seconds, period, status, ageMs, unavailableReason };
    }

    private async connect(): Promise<void> {
        const session = ++this.session;
        clearTimeout(this.retry);
        this.socket?.close();
        this.socket = undefined;
        this.awaitingFreshSample = true;
        this.seq = this.phoneSeq = -1;
        this.offsets = [];
        this.changed();
        try {
            const access = await this.env.links();
            if (!this.active || session !== this.session) return;
            if (!Number.isFinite(access.validUntil)) throw new Error('Invalid access expiry');
            // Retain an expired response's deadline so it receives the single renewal too.
            this.expires = access.validUntil;
            if (access.validUntil <= this.env.wallNow()) throw new Error('Expired access');
            const scoreboard = new URL(access.scoreboardUrl);
            if (!['https:', 'http:'].includes(scoreboard.protocol)) throw new Error('Invalid scoreboard origin');
            const token = scoreboard.searchParams.get('token');
            if (!token) throw new Error('Missing view access');
            const url = new URL(`/table/${this.matchId}/clock/live`, scoreboard.origin);
            url.protocol = scoreboard.protocol === 'https:' ? 'wss:' : 'ws:';
            url.searchParams.set('token', token);
            url.searchParams.set('clientId', this.clientId);
            url.searchParams.set('view', 'matchlog');
            const socket = this.env.socket(url.toString());
            this.socket = socket;
            const current = () => this.active && session === this.session;
            let opened = false;
            socket.onopen = () => {
                if (!current()) return;
                opened = true;
                this.hasConnected = true;
                this.accessUnavailable = false;
                this.status = this.sample ? 'Connecting' : 'No phone clock';
                this.changed();
            };
            socket.onmessage = event => { if (current()) this.receive(event.data); };
            const disconnected = () => {
                if (!current()) return;
                this.session++;
                socket.close();
                this.awaitingFreshSample = true;
                // Browsers hide the handshake HTTP status, including 403.
                const refused = !opened && (!this.hasConnected || this.accessRenewalUsed || this.env.wallNow() >= this.expires);
                this.accessUnavailable = refused;
                this.status = refused ? 'Access unavailable' : 'Disconnected';
                if (refused) this.changed();
                else this.scheduleReconnect();
            };
            socket.onclose = disconnected;
            socket.onerror = disconnected;
        } catch {
            if (!this.active || session !== this.session) return;
            this.accessUnavailable = true;
            this.status = 'Access unavailable';
            this.changed();
        }
    }

    private scheduleReconnect(): void {
        const delay = Math.min(30000, 1000 * Math.pow(2, Math.min(this.attempt++, 5))) * (0.8 + this.env.random() * 0.2);
        this.retry = setTimeout(() => { void this.connect(); }, delay);
        this.changed();
    }

    private receive(raw: string): void {
        try {
            const frame = JSON.parse(raw);
            if (frame.v !== 1 || frame.matchId !== this.matchId || !integer(frame.seq)
                || frame.seq < this.seq || !finite(frame.serverNow)) return;
            if (frame.clock === null) {
                this.sample = undefined;
                this.status = 'No phone clock';
                this.changed();
                return;
            }
            const clock = frame.clock;
            const updatedAt = Date.parse(frame.updatedAt);
            if (!clock || !integer(frame.phoneSeq) || frame.phoneSeq < 1 || !Number.isFinite(updatedAt)
                || updatedAt > frame.serverNow || !integer(clock.period) || clock.period < 1
                || !finite(clock.refAt) || clock.refAt > frame.serverNow || clock.refAt > updatedAt
                || !finite(clock.gameMs) || clock.gameMs < 0 || !finite(clock.segmentMinutes) || clock.segmentMinutes <= 0
                || typeof clock.running !== 'boolean' || !['game', 'break', 'timeout'].includes(clock.mode)) return;
            this.seq = frame.seq;
            // Viewer heartbeats and server supervision never refresh a phone sample.
            if (frame.phoneSeq <= this.phoneSeq || (this.phoneSeq >= 0 && this.sample && clock.refAt <= this.sample.refAt)) return;
            this.phoneSeq = frame.phoneSeq;
            const after = clock.breakPrompt?.afterPeriod ?? (clock.mode === 'break' ? clock.timeoutOrBreak?.afterPeriod : undefined);
            const hasBreak = clock.breakPrompt != null || clock.mode === 'break';
            const invalidBoundary = hasBreak && (!integer(after) || after < 1 || after !== clock.period
                || (clock.mode === 'break' && clock.timeoutOrBreak?.kind !== 'break')
                || (clock.breakPrompt && clock.timeoutOrBreak && clock.timeoutOrBreak.afterPeriod !== after));
            this.sampleReceived = this.env.monotonicNow();
            this.sampleWall = this.env.wallNow();
            this.offsets.push(frame.serverNow - this.sampleReceived);
            this.offsets = this.offsets.slice(-20);
            this.sampleServerOffset = Math.max(...this.offsets);
            this.awaitingFreshSample = false;
            this.sample = { period: clock.period, segmentMinutes: clock.segmentMinutes,
                gameMs: clock.gameMs, refAt: clock.refAt, running: clock.running, mode: clock.mode,
                breakAfter: hasBreak && !invalidBoundary ? after : undefined,
                incompatible: invalidBoundary || (clock.mode === 'timeout' && clock.timeoutOrBreak?.kind !== 'timeout') };
            if (frame.serverNow - clock.refAt < PHONE_STALE_MS) {
                this.attempt = 0;
                this.accessRenewalUsed = false;
            }
            this.changed();
        } catch { /* Malformed network input cannot change the selected clock. */ }
    }
}
